import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, AlertTriangle, Percent, Tag, StickyNote } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const REASON_CARDS = [
  { id: "commission", icon: Percent, title: "Commission Deduction", subtitle: "Agent has deducted their agreed commission before payment." },
  { id: "discount", icon: Tag, title: "Discount Applied", subtitle: "A discount was agreed and applied to this invoice." },
  { id: "short_payment", icon: AlertTriangle, title: "Short Payment", subtitle: "Client paid less than agreed — outstanding balance requires chasing." },
  { id: "other", icon: StickyNote, title: "Other", subtitle: "Another reason — add details in the notes field below." },
];

const DISCOUNT_REASONS = ["Goodwill Gesture", "Client Complaint", "Pricing Error", "Negotiated Reduction", "Repeat Client Discount", "Other"];

// Calculate commission breakdown from invoice total + commission_type string ("12.5+vat", "20+vat", "none").
export function calcCommission(invoiceTotalIncVat, commissionType, includesVat = true) {
  if (!commissionType || commissionType === "none") return null;
  const m = /^([\d.]+)/.exec(commissionType);
  if (!m) return null;
  const rate = parseFloat(m[1]);
  const VAT_MULT = 1.2;
  // The system invoices commission clients gross; the ex-VAT figure is total / 1.2.
  const invoiceExVat = invoiceTotalIncVat / VAT_MULT;
  // Commission is computed on the invoice EX VAT figure (agreed agency rate is always
  // applied to the ex-VAT amount in this business).
  const commissionExVat = +(invoiceExVat * (rate / 100)).toFixed(2);
  const commissionVat = includesVat ? +(commissionExVat * 0.2).toFixed(2) : 0;
  const totalCommission = +(commissionExVat + commissionVat).toFixed(2);
  const expectedNet = +(invoiceTotalIncVat - totalCommission).toFixed(2);
  return { rate, commissionExVat, commissionVat, totalCommission, expectedNet, includesVat };
}

export default function PaymentReceiptModal({
  open,
  onOpenChange,
  quote,
  pricingRules = {},
  isEdit = false,
  onSaved,
}) {
  const invoiceTotal = +(quote?.total_inc_vat || 0).toFixed(2);
  const clientType = quote?.client_type || "Private";
  const clientRule = pricingRules?.[clientType] || {};
  const commissionType = clientRule.commission_type || "none";
  const autoApplyCommission = clientRule.auto_apply_commission !== false && commissionType !== "none";

  const initialCommission = useMemo(
    () => (commissionType !== "none" ? calcCommission(invoiceTotal, commissionType, true) : null),
    [invoiceTotal, commissionType]
  );

  // Form state
  const [amountReceived, setAmountReceived] = useState(invoiceTotal);
  const [shortfallReason, setShortfallReason] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentMethod, setPaymentMethod] = useState("Bank Transfer");
  const [paymentReference, setPaymentReference] = useState("");
  const [notes, setNotes] = useState("");
  const [discountReason, setDiscountReason] = useState(DISCOUNT_REASONS[0]);
  const [manualCommission, setManualCommission] = useState(0);
  const [commissionIncludesVat, setCommissionIncludesVat] = useState(true);
  const [saving, setSaving] = useState(false);

  // (Re)initialise whenever the modal opens or the quote changes.
  useEffect(() => {
    if (!open) return;
    if (isEdit && quote?.invoice_paid) {
      const paidAmt = +(quote.invoice_paid_amount || 0).toFixed(2);
      setAmountReceived(paidAmt);
      setShortfallReason((quote.shortfall_reason || "").toLowerCase() === "full" ? "" : (quote.shortfall_reason || ""));
      setPaymentDate((quote.invoice_paid_date || new Date().toISOString()).slice(0, 10));
      setPaymentMethod(quote.payment_method || "Bank Transfer");
      setPaymentReference(quote.payment_reference || "");
      setNotes(quote.payment_notes || "");
      setDiscountReason(quote.discount_reason || DISCOUNT_REASONS[0]);
      setManualCommission(quote.total_commission_deducted || 0);
      setCommissionIncludesVat(quote.commission_includes_vat !== false);
    } else if (autoApplyCommission && initialCommission) {
      setShortfallReason("commission");
      setAmountReceived(initialCommission.expectedNet);
      setCommissionIncludesVat(initialCommission.includesVat);
      setPaymentDate(new Date().toISOString().slice(0, 10));
      setPaymentMethod("Bank Transfer");
      setPaymentReference("");
      setNotes("");
    } else {
      setAmountReceived(invoiceTotal);
      setShortfallReason("");
      setPaymentDate(new Date().toISOString().slice(0, 10));
      setPaymentMethod("Bank Transfer");
      setPaymentReference("");
      setNotes("");
      setCommissionIncludesVat(commissionType !== "none");
    }
  }, [open, isEdit, quote?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const shortfall = +(invoiceTotal - amountReceived).toFixed(2);
  const overpaid = amountReceived > invoiceTotal + 0.01;
  const isFull = Math.abs(shortfall) < 0.01;
  const needsReason = !isFull && !overpaid;

  const liveCommission = useMemo(
    () => (commissionType !== "none" ? calcCommission(invoiceTotal, commissionType, commissionIncludesVat) : null),
    [invoiceTotal, commissionType, commissionIncludesVat]
  );

  // Re-pre-fill amount received when user picks Commission and commission rate exists.
  useEffect(() => {
    if (shortfallReason === "commission" && liveCommission && !isEdit) {
      setAmountReceived(liveCommission.expectedNet);
    }
  }, [shortfallReason, liveCommission, isEdit]);

  const canSubmit = !overpaid && (isFull || shortfallReason !== "");

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error(overpaid ? "Amount exceeds invoice total" : "Please select a reason for the shortfall");
      return;
    }
    setSaving(true);
    try {
      const userName = localStorage.getItem("desktop_user_name") || "User";
      const payload = {
        payment_date: paymentDate,
        amount_paid: amountReceived,
        amount_received: amountReceived,
        invoice_total: invoiceTotal,
        shortfall_amount: Math.max(shortfall, 0),
        shortfall_reason: isFull ? "full" : shortfallReason,
        payment_method: paymentMethod,
        payment_reference: paymentReference,
        notes,
      };
      if (shortfallReason === "commission") {
        if (liveCommission) {
          payload.commission_rate = liveCommission.rate;
          payload.commission_amount_ex_vat = liveCommission.commissionExVat;
          payload.commission_vat = liveCommission.commissionVat;
          payload.total_commission_deducted = liveCommission.totalCommission;
          payload.commission_includes_vat = commissionIncludesVat;
        } else {
          payload.total_commission_deducted = manualCommission || shortfall;
        }
      }
      if (shortfallReason === "discount") {
        payload.discount_reason = discountReason;
      }
      await api.put(`/quotes/${quote.id}/payment?changed_by=${encodeURIComponent(userName)}`, payload);
      toast.success(isFull ? "Payment recorded" : (shortfallReason === "short_payment" ? "Partial payment recorded" : "Payment recorded with shortfall"));
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to record payment");
    } finally {
      setSaving(false);
    }
  };

  if (!quote) return null;

  return (
    <>
      {open && <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 998 }} />}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-h-[88vh] overflow-y-auto"
          style={{
            width: "min(600px, calc(100vw - 32px))",
            maxWidth: "calc(100vw - 32px)",
            background: "#ffffff",
            borderRadius: "12px",
            padding: "28px",
            zIndex: 999,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          }}
          data-testid="payment-receipt-modal"
        >
          <DialogHeader>
            <DialogTitle>{isEdit ? "Edit Payment Record" : "Record Payment"}</DialogTitle>
            <DialogDescription className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
              Invoice {quote.quote_number || "—"} — {quote.site_address || "—"} — Total: £{invoiceTotal.toFixed(2)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-3">
            {/* Field 1 — Amount Received */}
            <div>
              <Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Amount received (£)</Label>
              <Input
                type="number"
                step="0.01"
                value={amountReceived}
                onChange={(e) => setAmountReceived(parseFloat(e.target.value) || 0)}
                className="mt-1 rounded-lg font-mono text-base"
                style={{ border: "1px solid var(--border)" }}
                data-testid="pr-amount-received"
              />
              {isFull && (
                <p className="text-xs mt-1 inline-flex items-center gap-1 font-semibold" style={{ color: "#16a34a" }} data-testid="pr-full-confirmation">
                  <Check className="w-3.5 h-3.5" /> Full payment received
                </p>
              )}
              {!isFull && !overpaid && (
                <p className="text-xs mt-1 inline-flex items-center gap-1 font-semibold" style={{ color: "#D97706" }} data-testid="pr-shortfall-warning">
                  <AlertTriangle className="w-3.5 h-3.5" /> Shortfall of £{shortfall.toFixed(2)}
                </p>
              )}
              {overpaid && (
                <p className="text-xs mt-1 inline-flex items-center gap-1 font-semibold" style={{ color: "#DC2626" }} data-testid="pr-overpaid-warning">
                  <AlertTriangle className="w-3.5 h-3.5" /> Amount exceeds invoice total — please check
                </p>
              )}
            </div>

            {/* Field 2 — Reason for Shortfall (only when shortfall) */}
            {needsReason && (
              <div>
                <Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Reason for shortfall</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {REASON_CARDS.map((r) => {
                    const Icon = r.icon;
                    const active = shortfallReason === r.id;
                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setShortfallReason(r.id)}
                        className="rounded-lg p-3 text-left transition-all"
                        style={{
                          border: active ? "2px solid #E05C2A" : "1px solid var(--border)",
                          background: active ? "#FFF5F0" : "var(--surface)",
                        }}
                        data-testid={`pr-reason-${r.id}`}
                      >
                        <Icon className="w-4 h-4 mb-1.5" style={{ color: active ? "#E05C2A" : "var(--text-muted)" }} />
                        <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{r.title}</p>
                        <p className="text-[10px] mt-1 leading-snug" style={{ color: "var(--text-muted)" }}>{r.subtitle}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Field 3 — Commission Details */}
            {needsReason && shortfallReason === "commission" && (
              liveCommission ? (
                <div className="rounded-lg p-3 space-y-1.5 text-sm" style={{ background: "var(--bg)", border: "1px solid var(--border)" }} data-testid="pr-commission-breakdown">
                  <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Invoice total ex VAT:</span><span className="font-mono">£{(invoiceTotal / 1.2).toFixed(2)}</span></div>
                  <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Commission rate:</span><span className="font-mono">{liveCommission.rate}%</span></div>
                  <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>Commission ex VAT:</span><span className="font-mono">£{liveCommission.commissionExVat.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span style={{ color: "var(--text-muted)" }}>VAT on commission (20%):</span><span className="font-mono">£{liveCommission.commissionVat.toFixed(2)}</span></div>
                  <div className="flex justify-between font-semibold pt-1.5" style={{ borderTop: "1px dashed var(--border)" }}><span>Total commission deducted:</span><span className="font-mono">£{liveCommission.totalCommission.toFixed(2)}</span></div>
                  <div className="flex justify-between font-bold"><span>Expected net payment:</span><span className="font-mono" style={{ color: "#E05C2A" }}>£{liveCommission.expectedNet.toFixed(2)}</span></div>
                  <label className="flex items-center gap-2 pt-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={commissionIncludesVat} onChange={(e) => setCommissionIncludesVat(e.target.checked)} data-testid="pr-commission-vat-toggle" />
                    Commission includes VAT (e.g. Bairstow Eves, Hamptons)
                  </label>
                </div>
              ) : (
                <div>
                  <Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Commission amount deducted (£)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={manualCommission}
                    onChange={(e) => setManualCommission(parseFloat(e.target.value) || 0)}
                    className="mt-1 rounded-lg font-mono"
                    style={{ border: "1px solid var(--border)" }}
                    data-testid="pr-commission-manual"
                  />
                </div>
              )
            )}

            {/* Field 4 — Discount Details */}
            {needsReason && shortfallReason === "discount" && (
              <div className="space-y-2" data-testid="pr-discount-section">
                <div>
                  <Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Discount reason</Label>
                  <select value={discountReason} onChange={(e) => setDiscountReason(e.target.value)} className="w-full h-10 mt-1 px-3 rounded-lg text-sm" style={{ border: "1px solid var(--border)" }} data-testid="pr-discount-reason">
                    {DISCOUNT_REASONS.map((r) => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Discount amount (£)</Label>
                  <Input readOnly value={shortfall.toFixed(2)} className="mt-1 rounded-lg font-mono" style={{ border: "1px solid var(--border)", background: "var(--bg)" }} data-testid="pr-discount-amount" />
                </div>
              </div>
            )}

            {/* Field 5 — Short Payment Details */}
            {needsReason && shortfallReason === "short_payment" && (
              <div className="space-y-2" data-testid="pr-short-section">
                <div className="rounded-lg p-3 text-xs" style={{ background: "#FFFBEB", border: "1px solid #FCD34D", color: "#92400E" }}>
                  This payment will be recorded as partial. The outstanding balance of £{shortfall.toFixed(2)} will remain as unpaid and will continue to appear in your outstanding invoices and chase sequence.
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Reason for short payment (optional)</Label>
                  <Input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. client querying one line item, awaiting credit note."
                    className="mt-1 rounded-lg"
                    style={{ border: "1px solid var(--border)" }}
                    data-testid="pr-short-reason"
                  />
                </div>
              </div>
            )}

            {/* Fields 6 + 7 — Payment date + method */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Payment date</Label>
                <Input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} className="mt-1 rounded-lg" style={{ border: "1px solid var(--border)" }} data-testid="pr-payment-date" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Payment method</Label>
                <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="w-full h-10 mt-1 px-3 rounded-lg text-sm" style={{ border: "1px solid var(--border)" }} data-testid="pr-payment-method">
                  <option>Bank Transfer</option><option>Stripe</option><option>Cash</option><option>Cheque</option><option>Other</option>
                </select>
              </div>
            </div>

            {/* Field 8 — Reference */}
            <div>
              <Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Payment reference (optional)</Label>
              <Input value={paymentReference} onChange={(e) => setPaymentReference(e.target.value)} placeholder="e.g. bank transfer reference, cheque number." className="mt-1 rounded-lg" style={{ border: "1px solid var(--border)" }} data-testid="pr-payment-reference" />
            </div>

            {/* Field 9 — Notes (suppressed when short_payment shows its own input) */}
            {shortfallReason !== "short_payment" && (
              <div>
                <Label className="text-xs uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Notes (optional)</Label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Any additional notes about this payment." className="w-full mt-1 rounded-lg p-3 text-sm" style={{ border: "1px solid var(--border)" }} data-testid="pr-notes" />
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pr-cancel">Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving || !canSubmit} style={{ background: "#E05C2A", color: "#fff" }} data-testid="pr-submit">
              {saving ? "Recording..." : "Record Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
